import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  Alert,
  Chip,
  Stack,
  Collapse,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState } from 'react';

interface ProgressTrackerProps {
  progress: {
    status: 'parsing' | 'analyzing' | 'generating' | 'completed' | 'error';
    progress: number;
    currentStep: string;
    error?: string;
  };
}

export default function ProgressTracker({ progress }: ProgressTrackerProps) {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const getStatusColor = () => {
    switch (progress.status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = () => {
    switch (progress.status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <AutorenewIcon sx={{ animation: 'spin 2s linear infinite' }} />;
    }
  };

  const getStatusLabel = () => {
    switch (progress.status) {
      case 'parsing':
        return 'Parsing RFP';
      case 'analyzing':
        return 'Analyzing Requirements';
      case 'generating':
        return 'Generating Proposal';
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Error';
      default:
        return 'Processing';
    }
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="h3">
              Generation Progress
            </Typography>
            <Chip
              icon={getStatusIcon()}
              label={getStatusLabel()}
              color={getStatusColor()}
              size="small"
            />
          </Box>

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                {progress.currentStep}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {progress.progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          {progress.error && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              sx={{
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  An error occurred during proposal generation
                </Typography>

                <Button
                  size="small"
                  onClick={() => setShowErrorDetails(!showErrorDetails)}
                  endIcon={showErrorDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    mt: 1,
                    mb: showErrorDetails ? 1 : 0,
                    textTransform: 'none',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {showErrorDetails ? 'Hide Details' : 'Show Details'}
                </Button>

                <Collapse in={showErrorDetails}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'rgba(0,0,0,0.1)',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      wordBreak: 'break-word',
                      overflowX: 'auto'
                    }}
                  >
                    {progress.error}
                  </Box>

                  <Typography variant="caption" display="block" mt={2} color="text.secondary">
                    If this issue persists, please contact support or check the logs for more information.
                  </Typography>
                </Collapse>
              </Box>
            </Alert>
          )}
        </Stack>
      </CardContent>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Card>
  );
}
